import qs from 'qs';

import { getWindow, getWindowSafe } from '@change-corgi/core/window';

export { isShareCancellation } from './isShareCancellation';

type NavigatorShare = {
	share: Window['navigator']['share'];
	canShare: Window['navigator']['canShare'];
};

const SIMULATION: NavigatorShare = {
	share: async (shareData) => {
		const details = [
			shareData?.title && `Title: ${shareData.title}`,
			shareData?.text && `Text: ${shareData.text}`,
			shareData?.url && `URL: ${shareData.url}`,
			shareData?.files?.length && `Files: ${shareData.files.map((file) => file.name).join(', ')}`,
		].filter(Boolean);
		// eslint-disable-next-line no-alert
		alert(`Simulated native share action. This is not a real share action.\n\n${details.join('\n')}`);
		return Promise.resolve();
	},
	canShare: () => true,
};

/**
 * returns an interface similar to the native navigator.share/canShare interface but with:
 * - undefined when the native share is not available
 * - a simulated share interface when native share is not available on the device (e.g. devtools) if the url contains `simulate_native_share=true`
 *
 * For instance, to test the share functionality in a browser that does not support the native share API,
 * you can add `?simulate_native_share=true` to the URL. This will display an alert instead of actually
 * sharing the content, but will allow you to test the share flow without having to use a real mobile device.
 *
 * The simulated share interface can also be enabled by setting `window.simulateNativeShare = true` in the console.
 */
export function getNavigatorShareInterface(): NavigatorShare | undefined {
	const window = getWindowSafe();
	if (!window) {
		return undefined;
	}
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (!window.navigator.canShare || !window.navigator.share) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
		if ((window as any).simulateNativeShare) {
			return SIMULATION;
		}
		const query = qs.parse(window.location.search, { ignoreQueryPrefix: true });
		if (query.simulate_native_share === 'true') {
			// persisting the simulation state in the window object in case the user navigates to another page
			// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
			(window as any).simulateNativeShare = true;
			return SIMULATION;
		}
		return undefined;
	}
	return window.navigator;
}

/**
 * Opens a URL by clicking a programmatic <a> element.
 *
 * Prefer this over window.open() or location.href for custom URL schemes (mailto:, sms:, etc.).
 * window.open() fails silently on iOS browsers other than Safari (e.g. Firefox), and location.href
 * may be blocked by WKWebView's navigation policy. Clicking a programmatic <a> element is treated
 * as a user-originated navigation by WKWebView, making it more reliably trigger the OS URL scheme
 * handler across all iOS browsers.
 */
export function openUrlWithHiddenAnchor(url: string, options: { newTab?: boolean } = {}): void {
	const win = getWindow();
	const a = win.document.createElement('a');
	a.href = url;
	a.style.display = 'none';
	if (options.newTab) {
		a.target = '_blank';
		a.rel = 'noopener noreferrer';
	}
	win.document.body.appendChild(a);
	a.click();
	win.document.body.removeChild(a);
}
